import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { appsAPI } from '../../services/api'
import { LexicalEditor } from '@werkstatt/lexical-editor'

import { logger } from '@/utils/logger';

const EMPTY_FORM = {
  name: '',
  slug: '',
  description: '',
  short_description: '',
  icon_url: '',
  image_url: '',
  features: '',
  price_monthly: '0.00',
  price_yearly: '0.00',
  is_active: true,
  is_featured: false,
  order_index: 0,
  demo_url: '',
  documentation_url: '',
  login_url: ''
}

export default function AppEditPage() {
  const { appId } = useParams()
  const navigate = useNavigate()
  const isEditing = !!appId

  const [loading, setLoading] = useState(isEditing)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [formData, setFormData] = useState(EMPTY_FORM)

  useEffect(() => {
    if (!isEditing) return
    appsAPI.getById(appId)
      .then((res) => {
        const app = res.data
        setFormData({
          name: app.name,
          slug: app.slug,
          description: app.description || '',
          short_description: app.short_description || '',
          icon_url: app.icon_url || '',
          image_url: app.image_url || '',
          features: app.features || '',
          price_monthly: app.price_monthly,
          price_yearly: app.price_yearly,
          is_active: app.is_active,
          is_featured: app.is_featured,
          order_index: app.order_index,
          demo_url: app.demo_url || '',
          documentation_url: app.documentation_url || '',
          login_url: app.login_url || ''
        })
      })
      .catch((err) => {
        setError('Fehler beim Laden der App')
        logger.error(err)
      })
      .finally(() => setLoading(false))
  }, [appId])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSaving(true)

    try {
      if (isEditing) {
        await appsAPI.update(appId, formData)
      } else {
        await appsAPI.create(formData)
      }
      navigate('/admin/apps')
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Speichern der App')
      logger.error(err)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
        <div className="wc-spinner" />
      </div>
    )
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <button
          onClick={() => navigate('/admin/apps')}
          className="wc-btn wc-btn-ghost"
          style={{ padding: 0, marginBottom: 8 }}
        >
          ← Zurück zu Apps
        </button>
        <h1 style={{ fontSize: 24, margin: 0 }}>{isEditing ? `App bearbeiten: ${formData.name}` : 'Neue App'}</h1>
      </div>

      {error && <div className="wc-alert wc-alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      <form onSubmit={handleSubmit} className="wc-card wc-elev-sm" style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 860 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
          <div className="wc-field">
            <label>App-Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="wc-input"
              required
            />
          </div>

          <div className="wc-field">
            <label>Slug (URL) *</label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              className="wc-input"
              required
              placeholder="z.B. reifenhotel-app"
            />
          </div>

          <div className="wc-field" style={{ gridColumn: 'span 2' }}>
            <label>Kurzbeschreibung</label>
            <input
              type="text"
              value={formData.short_description}
              onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
              className="wc-input"
              placeholder="Kurze Beschreibung für Kacheln"
            />
          </div>

          <div className="wc-field" style={{ gridColumn: 'span 2' }}>
            <label>Beschreibung (Rich-Text-Editor)</label>
            <div style={{ background: 'var(--wc-neutral-100)', borderRadius: 'var(--wc-radius-md)', overflow: 'hidden' }}>
              <LexicalEditor
                value={formData.description}
                onChange={(value) => setFormData({ ...formData, description: value })}
                placeholder="Ausführliche Beschreibung der App..."
                accentColor="green"
              />
            </div>
          </div>

          <div className="wc-field" style={{ gridColumn: 'span 2' }}>
            <label>Features (JSON Format)</label>
            <textarea
              value={formData.features}
              onChange={(e) => setFormData({ ...formData, features: e.target.value })}
              className="wc-input wc-mono"
              rows={3}
              placeholder='["Feature 1", "Feature 2", "Feature 3"]'
            />
          </div>

          <div className="wc-field">
            <label>Preis Monatlich (€)</label>
            <input
              type="number"
              step="0.01"
              value={formData.price_monthly}
              onChange={(e) => setFormData({ ...formData, price_monthly: e.target.value })}
              className="wc-input"
            />
          </div>

          <div className="wc-field">
            <label>Preis Jährlich (€)</label>
            <input
              type="number"
              step="0.01"
              value={formData.price_yearly}
              onChange={(e) => setFormData({ ...formData, price_yearly: e.target.value })}
              className="wc-input"
            />
          </div>

          <div className="wc-field">
            <label>Icon URL</label>
            <input
              type="url"
              value={formData.icon_url}
              onChange={(e) => setFormData({ ...formData, icon_url: e.target.value })}
              className="wc-input"
              placeholder="https://example.com/icon.png"
            />
          </div>

          <div className="wc-field">
            <label>Bild URL</label>
            <input
              type="url"
              value={formData.image_url}
              onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
              className="wc-input"
              placeholder="https://example.com/image.png"
            />
          </div>

          <div className="wc-field">
            <label>Login-URL (App-Adresse)</label>
            <input
              type="url"
              value={formData.login_url}
              onChange={(e) => setFormData({ ...formData, login_url: e.target.value })}
              className="wc-input"
              placeholder="https://reifenhotel.werkstatt-connect.de/login"
            />
            <p className="wc-muted" style={{ fontSize: 11, margin: '6px 0 0' }}>
              Wird im Kundenportal als "Zur App" / "Anmelden" Link angezeigt
            </p>
          </div>

          <div className="wc-field">
            <label>Demo URL</label>
            <input
              type="url"
              value={formData.demo_url}
              onChange={(e) => setFormData({ ...formData, demo_url: e.target.value })}
              className="wc-input"
              placeholder="https://demo.example.com"
            />
          </div>

          <div className="wc-field">
            <label>Dokumentation URL</label>
            <input
              type="url"
              value={formData.documentation_url}
              onChange={(e) => setFormData({ ...formData, documentation_url: e.target.value })}
              className="wc-input"
              placeholder="https://docs.example.com"
            />
          </div>

          <div className="wc-field">
            <label>Sortierreihenfolge</label>
            <input
              type="number"
              value={formData.order_index}
              onChange={(e) => setFormData({ ...formData, order_index: parseInt(e.target.value) })}
              className="wc-input"
            />
          </div>

          <div style={{ gridColumn: 'span 2', display: 'flex', gap: 24 }}>
            <label className="wc-check">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
              Aktiv
            </label>

            <label className="wc-check">
              <input
                type="checkbox"
                checked={formData.is_featured}
                onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
              />
              Featured (Hervorgehoben)
            </label>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          <button type="button" onClick={() => navigate('/admin/apps')} className="wc-btn wc-btn-secondary">
            Abbrechen
          </button>
          <button type="submit" className="wc-btn wc-btn-primary" disabled={saving}>
            {saving ? 'Speichern…' : (isEditing ? 'Speichern' : 'Erstellen')}
          </button>
        </div>
      </form>
    </div>
  )
}
